import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageSquareText } from "lucide-react";
import api from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { useSettings } from "../context/SettingsContext";
import { EmailCapture } from "../components/EmailCapture";
import { GuideCard } from "../components/GuideCard";

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] } }),
};

export default function Home() {
  const { settings } = useSettings();
  useSeo({
    title: "Practical AI for Everyday Life — I stay on top of AI so you don't have to",
    description: "Free, plain-English AI setups you can copy and use in seconds. Real builds, no hype, from BJ (@everydayai_foryou).",
  });

  const { data: featured = [] } = useQuery({
    queryKey: ["featured"],
    queryFn: () => api.get("/guides", { params: { featured: true } }).then((r) => r.data),
  });

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-navy grain" data-testid="hero">
        <img
          src="/brand/bj-hero-wide.png"
          alt="BJ McCreary, host of Practical AI for Everyday Life"
          width="1600"
          height="900"
          className="absolute inset-0 hidden h-full w-full object-cover object-right-top md:block"
          data-testid="hero-avatar"
          draggable="false"
        />
        {/* legibility scrim (navy → transparent, left to right) — desktop only */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{ background: "linear-gradient(90deg, #16283C 0%, rgba(22,40,60,0.92) 34%, rgba(22,40,60,0.55) 60%, rgba(22,40,60,0.05) 100%)" }}
        />
        <div className="container-brand relative flex min-h-[560px] flex-col justify-center py-14 md:min-h-[640px] md:py-28">
          <div className="max-w-2xl">
            {/* Welcome heading (single H1) + mobile-only circular avatar */}
            <div className="mb-1 flex items-center gap-4">
              <h1 className="flex-1 text-[34px] leading-[1.04] text-bone md:text-6xl md:leading-[1.0] lg:text-[72px] lg:leading-[0.98]" data-testid="hero-heading">
                Welcome to <span className="italic text-ochre-bright">Practical AI for Everyday Life</span>
              </h1>
              <img
                src="/brand/av-color.png"
                alt="BJ McCreary, host of Practical AI for Everyday Life"
                width="96"
                height="96"
                className="h-24 w-24 shrink-0 rounded-full object-cover shadow-hero ring-2 ring-ochre-bright/70 md:hidden"
                data-testid="hero-avatar-mobile"
                draggable="false"
              />
            </div>
            <motion.p
              custom={1}
              variants={rise}
              initial="hidden"
              animate="show"
              className="mt-4 text-2xl leading-tight text-bone md:mt-5 md:text-3xl lg:text-4xl"
              data-testid="hero-tagline"
            >
              I stay on top of AI <span className="italic text-ochre-bright">so you don't have to.</span>
            </motion.p>

            <motion.p custom={4} variants={rise} initial="hidden" animate="show" className="mt-7 max-w-measure text-lg text-on-navy-body">
              Hi, I'm BJ McCreary. I'm passionate about making AI simple, practical, and accessible for
              everyone. I cut through the hype to share real-world prompts, tools, and workflows that help you
              save time, work smarter, and unlock what's possible with AI.
            </motion.p>
            <motion.p custom={4.5} variants={rise} initial="hidden" animate="show" className="mt-4 max-w-measure text-lg text-on-navy-body">
              Whether you're just getting started or already using AI every day, you'll find practical
              resources here to help you stay ahead.
            </motion.p>

            <motion.div custom={5} variants={rise} initial="hidden" animate="show" className="on-navy mt-9 flex flex-wrap items-center gap-4">
              <Link to="/subscribe" className="btn btn-primary" data-testid="hero-cta-primary">
                Get the free setups <ArrowRight size={16} />
              </Link>
              <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="hero-cta-secondary">
                Watch on YouTube
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ COMMENT-WORD STRIP ============ */}
      <section className="border-y-2 border-navy bg-ochre" data-testid="comment-strip">
        <div className="container-brand flex flex-col gap-4 py-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy text-ochre-bright">
              <MessageSquareText size={20} />
            </span>
            <p className="max-w-none text-[17px] font-medium text-navy">
              <strong>Saw a video that said "comment a word"?</strong> The setup lives right here.
            </p>
          </div>
          <Link to="/setups" className="btn btn-secondary whitespace-nowrap" data-testid="comment-strip-cta">
            Browse all setups <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ============ MOCHI TEASER ============ */}
      <section className="sheet grain border-b border-navy/10" data-testid="mochi-teaser">
        <div className="container-brand py-20">
          <div className="grid items-stretch overflow-hidden rounded-card border border-navy/12 bg-navy shadow-hero md:grid-cols-[.72fr_1.28fr]">
            <div className="flex flex-col justify-center p-8 md:p-10">
              <p className="eyebrow !text-ochre-bright">Meet Mochi</p>
              <h2 className="mt-3 text-3xl leading-[1.1] text-bone md:text-4xl">
                My AI agent, <span className="italic text-ochre-bright">built in the open.</span>
              </h2>
              <p className="mt-4 max-w-md text-[15px] text-on-navy-body">
                Mochi remembers, learns while I sleep, reaches out on her own, and takes real action — not just
                chat. Peek inside how her brain actually works.
              </p>
              <Link to="/my-ai" className="btn btn-primary mt-6 self-start" data-testid="mochi-teaser-cta">
                Learn about Mochi <ArrowRight size={16} />
              </Link>
            </div>
            <Link to="/my-ai" className="group block overflow-hidden border-t border-white/10 md:border-l md:border-t-0" data-testid="mochi-teaser-image-link">
              <img
                src="/brand/mochi-brain.webp"
                alt="Mochi's brain — interactive map"
                className="block h-auto w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                data-testid="mochi-teaser-image"
                draggable="false"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FEATURED (BENTO) ============ */}
      <section className="sheet grain" data-testid="featured-section">
        <div className="container-brand py-24">
          <div className="mb-12 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="eyebrow">The library</p>
              <h2 className="mt-3 max-w-xl text-4xl leading-[1.05] md:text-5xl">
                Free setups, <span className="italic text-ochre">ready to paste.</span>
              </h2>
            </div>
            <Link to="/setups" className="prose-link font-sans text-[13px] font-bold uppercase tracking-[0.12em]">
              See the whole library →
            </Link>
          </div>
          <div className="grid gap-x-6 gap-y-12 pt-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((g, i) => (
              <GuideCard key={g.slug} guide={g} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT — editorial pull-quote ============ */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="about-teaser">
        <div className="container-brand relative grid items-center gap-12 py-24 md:grid-cols-[1.3fr_.7fr]">
          <div>
            <span className="giant-quote block text-[120px] md:text-[160px]">&ldquo;</span>
            <blockquote className="-mt-10 max-w-2xl text-3xl leading-[1.25] text-bone md:text-[40px] md:leading-[1.2]">
              By day I'm a Solution Engineer. Off the clock, I'm obsessed with making AI
              <span className="italic text-ochre-bright"> genuinely useful</span> for everyday people — and I share exactly what works.
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <img src="/brand/av-color.png" alt="BJ" className="h-14 w-14 rounded-full ring-2 ring-ochre-bright" />
              <div>
                <p className="font-sans text-[13px] font-bold uppercase tracking-[0.16em] text-bone">BJ</p>
                <p className="text-[13px] text-on-navy-muted">Host · @everydayai_foryou</p>
              </div>
              <Link to="/about" className="on-navy ml-4 inline-flex items-center gap-1 font-sans text-[13px] font-bold uppercase tracking-[0.12em] text-ochre-bright">
                My story <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
          <div className="hidden justify-end md:flex">
            <img src="/brand/bj-about.webp" alt="BJ" className="w-full max-w-[300px] rotate-2 rounded-card object-cover shadow-hero" />
          </div>
        </div>
      </section>

      {/* ============ EMAIL BAND ============ */}
      <section className="bg-warm grain border-t border-navy/10" data-testid="email-band">
        <div className="container-brand grid items-center gap-10 py-20 md:grid-cols-[1fr_1fr]">
          <div>
            <p className="eyebrow">Don't miss one</p>
            <h2 className="mt-3 text-4xl leading-[1.05] md:text-5xl">
              New setups, <span className="italic text-ochre">straight to you.</span>
            </h2>
            <p className="mt-4 max-w-md text-text-body">Join the list and get each free setup the moment it drops. No spam, unsubscribe anytime.</p>
          </div>
          <div className="brand-card p-8">
            <EmailCapture source="home-band" heading="" sub="" compact redirectToThankYou />
            <p className="mt-3 text-[13px] text-text-muted">One plain-English setup at a time. No hype.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
